/**
 * How late a court is running — one rule for the web schedule page and the
 * app's schedule payload (`eventModelV2.getPublicSchedule` → `/api/app/events/
 * [eventId]/schedule` `courts[].delay_min`).
 *
 * A court is late only once its live match is still on court PAST the time the
 * NEXT match there was due to start. The live match's own playing time is not
 * lateness: a 10:00 match that is 30 minutes in at 10:30, with the next match
 * due at 10:45, is on time. Before 2026-10-30 the model reported those 30
 * minutes as the delay, so every on-time event read "Est." in the app.
 *
 * Known limits, inherited by the app by design: the rule understates lateness
 * once a match starts late (a 10:00 match that began at 11:00 reads as 20 min
 * late at 11:05 although the 10:45 match will really start nearer 12:00), and
 * "next" is whatever row is still `upcoming` by the caller's status derivation.
 */

/** A very stale instant must not print an absurd number. */
export const MAX_DELAY_MIN = 240;

const MIN = 60_000;

/** The two fields the rule reads; `PublicMatch` and the model's rows both satisfy it. */
export type DelayMatch = {
  scheduledAt: string;
  status: 'live' | 'upcoming' | 'completed';
};

/**
 * Minutes a court is running late: only once its live match is still on court past the time the
 * NEXT match there was due. The live match's own playing time is not lateness. Null with nothing
 * live, nothing behind it, or once caught up. `courtMatches` are one court's matches.
 */
export function courtDelayMinutes(courtMatches: DelayMatch[], nowMs: number): number | null {
  const sorted = [...courtMatches].sort(
    (a, b) => Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt),
  );
  const liveIdx = sorted.findIndex((m) => m.status === 'live');
  if (liveIdx === -1) return null;
  const next = sorted.slice(liveIdx + 1).find((m) => m.status === 'upcoming');
  if (!next) return null;
  const minutes = Math.round((nowMs - Date.parse(next.scheduledAt)) / MIN);
  return minutes <= 0 ? null : Math.min(minutes, MAX_DELAY_MIN);
}
